import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];

const STATUS_STYLES = {
  pending: "text-white/60 border-white/15",
  confirmed: "text-silver border-silver/50",
  packed: "text-silver border-silver/50",
  shipped: "text-denim-light border-denim-light/50",
  delivered: "text-white border-white/40",
  cancelled: "text-white/40 border-white/10",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  const load = async () => {
    setLoading(true);
    const params = filter === "All" ? {} : { params: { status: filter.toLowerCase() } };
    try {
      const r = await api.get("/admin/orders", params);
      setOrders(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const updateStatus = async (order_id, status) => {
    try {
      await api.put(`/admin/orders/${order_id}/status`, { status });
      toast.success(`Order ${order_id} → ${status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div>
        <div className="eyebrow text-silver/70 mb-2">Fulfilment</div>
        <h1 className="font-serif text-white text-3xl md:text-4xl">Orders.</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {["All", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s === "All" ? "All" : s)}
            data-testid={`orders-filter-${s.toLowerCase()}`}
            className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase border ${
              filter === s ? "bg-white text-black border-white" : "border-white/15 text-white/70 hover:border-white/40"
            }`}
          >{s}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="text-white/50">No orders.</div>
      ) : (
        <ul className="space-y-4">
          {orders.map((o) => (
            <li key={o.order_id} className="border border-white/8 bg-midnight p-5" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70">{o.order_id}</div>
                  <div className="text-white/50 text-xs mt-1">{new Date(o.created_at).toLocaleString()}</div>
                  <div className="text-white/70 text-sm mt-2">
                    {o.shipping_address?.full_name} · {o.user_email || o.user_phone}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-silver text-lg">{o.currency} {new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(o.total)}</div>
                  <span className={`px-3 py-1 text-[10px] tracking-[0.3em] uppercase border ${STATUS_STYLES[o.status] || "text-white/60 border-white/20"}`}>
                    {o.status}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70 mb-2">Items</div>
                  <ul className="space-y-1 text-sm text-white/80">
                    {o.items.map((it, i) => (
                      <li key={i}>{it.quantity}× {it.name} {it.size ? `· ${it.size}` : ""} {it.color ? `· ${it.color}` : ""}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-silver/70 mb-2">Shipping to</div>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {o.shipping_address?.full_name}<br />
                    {o.shipping_address?.line1}{o.shipping_address?.line2 ? ", " + o.shipping_address.line2 : ""}<br />
                    {o.shipping_address?.city}, {o.shipping_address?.state} {o.shipping_address?.postal_code}<br />
                    {o.shipping_address?.country}<br />
                    Phone: {o.shipping_address?.phone}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 border-t border-white/8 pt-4">
                <span className="text-[10px] tracking-[0.3em] uppercase text-silver/70 mr-2">Update status</span>
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => updateStatus(o.order_id, s)}
                    disabled={o.status === s}
                    data-testid={`set-${o.order_id}-${s}`}
                    className={`px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase border ${
                      o.status === s ? "bg-silver text-black border-silver cursor-default" : "border-white/15 text-white/70 hover:border-white"
                    }`}
                  >{s}</button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
